import { useQuery, UseQueryOptions } from "react-query";
import axios from "axios";
import { END_POINT } from "config/environment";

export interface ICity {
    name: string;
    country: string;
}

export interface ISector {
    id: number;
    name: string;
}

export interface IJobTitle {
    id: number;
    name: string;
}

export interface ISkill {
    id: number;
    name: string;
}

// Cache configuration
const CACHE_CONFIG = {
    staleTime: 1000 * 60 * 60 * 24,
    cacheTime: 1000 * 60 * 60 * 48,
};


export const useGetCities = (country?: string, token?: string) => {
    return useQuery(
        ["cities", country],
        async () => {
            if (!country) return [];

            const response = await axios.post(
                "https://countriesnow.space/api/v0.1/countries/cities",
                { country }
            );

            return response.data.data.map((city: string) => ({
                name: city,
                country,
            }));
        },
        {
            ...CACHE_CONFIG,
            enabled: !!country, // Only fetch when country is selected
        }
    );
};

// Sectors cache (using your existing hook with caching)
export const useGetSectors = (token?: string) => {
    return useQuery(
        ["sectors"],
        async () => {
            const response = await axios.get(`${END_POINT.BASE_URL}/v1/sectors`, {
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data as ISector[];
        },
        {
            ...CACHE_CONFIG,
            enabled: !!token,
        }
    );
};

export const useGetSkillsByJobTitle = (
    jobTitleId?: number | null,
    token?: string,
    options?: UseQueryOptions<ISkill[]>
) => {

    return useQuery<ISkill[]>(
        ["skills-by-jobtitle", jobTitleId],
        async () => {
            if (!jobTitleId) return [];

            const res = await axios.get(`${END_POINT.BASE_URL}/v1/skills`, {
                params: { jobtitle_id: jobTitleId },
                headers: { Authorization: `Bearer ${token}` },
            });
            return res.data;
        },
        {
            enabled: !!jobTitleId && !!token,
            staleTime: 1000 * 60 * 60 * 24,
            ...options,
        }
    );
};

// Job titles cache
export const useGetJobTitles = (token?: string) => {
    return useQuery(
        ["job-titles"],
        async () => {
            const response = await axios.get(`${END_POINT.BASE_URL}/v1/jobtitles`, {
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data as IJobTitle[];
        },
        {
            ...CACHE_CONFIG,
            enabled: !!token,
        }
    );
};


// Combined prefetch function
export const prefetchAppData = async (queryClient: any, token: string) => {
    await Promise.all([
        queryClient.prefetchQuery(["sectors"], async () => {
            const res = await axios.get(`${END_POINT.BASE_URL}/v1/sectors`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            return res.data;
        }),

        queryClient.prefetchQuery(["job-titles"], async () => {
            const res = await axios.get(`${END_POINT.BASE_URL}/v1/jobtitles`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            return res.data;
        })
    ]);
};